import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Share, Plus, Smartphone } from "lucide-react";

const DISMISSED_KEY = "nd_pwa_install_dismissed";
const DELAY_MS = 8000;

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // iPadOS 13+ reports as Mac; detect via touch points
  const iPadOS =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPWABanner() {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState(null); // 'android' | 'ios'
  const deferredPromptRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone()) return; // already installed
    if (localStorage.getItem(DISMISSED_KEY)) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setPlatform("android");
      setTimeout(() => setVisible(true), DELAY_MS);
    };

    const onInstalled = () => {
      setVisible(false);
      deferredPromptRef.current = null;
      try {
        localStorage.setItem(DISMISSED_KEY, "installed");
      } catch (_) {
        /* localStorage may be blocked */
      }
      // Report install to local backend counter (best-effort)
      try {
        const platform = isIOS() ? "ios" : "android";
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/app/pwa-install`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ platform, source: "appinstalled" }),
          keepalive: true,
        }).catch(() => {});
      } catch (_) { /* noop */ }
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari does not fire beforeinstallprompt -> show manual banner
    if (isIOS()) {
      setPlatform("ios");
      const t = setTimeout(() => setVisible(true), DELAY_MS);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch (_) {
      /* localStorage may be blocked */
    }
    setVisible(false);
  };

  const install = async () => {
    const prompt = deferredPromptRef.current;
    if (!prompt) return;
    try {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === "accepted") {
        try {
          localStorage.setItem(DISMISSED_KEY, "accepted");
        } catch (_) {
          /* localStorage may be blocked */
        }
      }
    } catch (_) {
      /* noop */
    } finally {
      deferredPromptRef.current = null;
      setVisible(false);
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="pwa-install-banner"
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.96 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed z-[60] bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-[380px]"
        >
          <div className="relative bg-card border border-border rounded-3xl shadow-[0_24px_60px_-20px_rgba(42,36,30,0.45)] p-5 grain overflow-hidden">
            <button
              data-testid="pwa-install-dismiss"
              onClick={dismiss}
              aria-label="Dismiss"
              className="absolute top-3 right-3 grid place-items-center w-8 h-8 rounded-full hover:bg-secondary transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-4">
              <span className="grid place-items-center w-14 h-14 rounded-2xl bg-primary text-primary-foreground arch shrink-0">
                <Smartphone className="w-7 h-7" />
              </span>
              <div className="min-w-0 pr-6">
                <p className="font-display font-extrabold text-lg tracking-tight leading-tight">
                  Install NearbyDeals
                </p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {platform === "ios"
                    ? "Add it to your Home Screen for a native-app feel."
                    : "One tap to install — no App Store needed."}
                </p>
              </div>
            </div>

            {platform === "android" && (
              <div className="flex items-center gap-2 mt-4">
                <button
                  onClick={install}
                  data-testid="pwa-install-cta"
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-2.5 hover:bg-primary/90 transition-colors"
                >
                  <Download className="w-4 h-4" /> Install app
                </button>
                <button
                  data-testid="pwa-install-later"
                  onClick={dismiss}
                  className="rounded-full border border-border font-semibold px-4 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors"
                >
                  Later
                </button>
              </div>
            )}

            {platform === "ios" && (
              <div
                data-testid="pwa-install-ios-hint"
                className="mt-4 rounded-2xl bg-secondary/60 border border-border p-3 text-sm text-foreground/90 leading-relaxed"
              >
                <p className="flex items-center gap-2">
                  <span className="grid place-items-center w-6 h-6 rounded-md bg-background border border-border">
                    1
                  </span>
                  Tap
                  <Share className="w-4 h-4 text-primary" />
                  <span className="font-semibold">Share</span> in Safari.
                </p>
                <p className="flex items-center gap-2 mt-1.5">
                  <span className="grid place-items-center w-6 h-6 rounded-md bg-background border border-border">
                    2
                  </span>
                  Choose
                  <Plus className="w-4 h-4 text-primary" />
                  <span className="font-semibold">Add to Home Screen</span>.
                </p>
                <div className="flex justify-end mt-2">
                  <button
                    data-testid="pwa-install-ios-dismiss"
                    onClick={dismiss}
                    className="text-sm font-semibold text-primary hover:opacity-80 transition-opacity"
                  >
                    Got it
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
